import React, { useEffect, useMemo, useState } from 'react'
import {
  Drawer,
  Input,
  InputNumber,
  Select
} from 'antd'
import Button from '../../../components/common/button/button'
import './addCategoryDrawer.scss'

const { TextArea } = Input

const getInitialForm = (initialValues = {}, parentKey = null) => {
  const safeInitialValues = initialValues || {}

  return {
    parentKey: parentKey || safeInitialValues.parentKey || null,
    name: safeInitialValues.name || '',
    slug: safeInitialValues.slug || '',
    description: safeInitialValues.description || '',
    sortOrder: safeInitialValues.sortOrder || 0,
    status: safeInitialValues.status || 'Active'
  }
}

const AddCategoryDrawer = ({
  open,
  mode,
  title,
  forceRoot = false,
  fixedParentCategoryName = '',
  hideParentSelector = false,
  initialValues = null,
  parentKey,
  parentOptions = [],
  onClose,
  onSave
}) => {
  const [formValues, setFormValues] = useState(getInitialForm(initialValues, parentKey))

  useEffect(() => {
    if (open) {
      setFormValues(getInitialForm(initialValues, parentKey))
    }
  }, [open, initialValues, parentKey])

  const panelTitle = useMemo(() => {
    if (title) return title
    if (mode === 'rename') return 'Edit Category'
    if (mode === 'add-child') return 'Add Subcategory'
    return 'Add Category'
  }, [title, mode])

  const autoSlug = useMemo(() => {
    return formValues.slug || formValues.name.trim().toLowerCase().replace(/\s+/g, '-')
  }, [formValues.slug, formValues.name])

  const setField = (key, value) => {
    setFormValues((prev) => ({
      ...prev,
      [key]: value
    }))
  }

  const handleSave = () => {
    const cleanedName = formValues.name.trim()

    if (!cleanedName) {
      return
    }

    onSave({
      ...formValues,
      parentKey: forceRoot ? null : formValues.parentKey,
      name: cleanedName,
      slug: autoSlug
    })
  }

  return (
    <Drawer
      open={open}
      title={panelTitle}
      onClose={onClose}
      width={430}
      destroyOnClose
      className="add-category-drawer"
    >
      <div className="drawer-form">
                <label>Parent Category</label>
                {hideParentSelector ? (
                  <Input
                    value={forceRoot ? 'None (Root Category)' : fixedParentCategoryName}
                    disabled
                  />
                ) : (
                  <Select
                    value={forceRoot ? null : formValues.parentKey}
                    onChange={(value) => setField('parentKey', value)}
                    options={[{ value: null, label: 'None (Root Category)' }, ...parentOptions]}
                    disabled={forceRoot || mode === 'edit'}
                  />
                )}
                <p className="field-helper">
                  {hideParentSelector
                    ? 'Parent category is selected from your action.'
                    : 'Select a parent category to create a subcategory.'}
                </p>

                <label>Category Name <span className="required">*</span></label>
                <Input
                  value={formValues.name}
                  placeholder="Enter category name"
                  maxLength={60}
                  showCount
                  onChange={(event) => setField('name', event.target.value)}
                />

                <label>Description</label>
                <TextArea
                  rows={3}
                  maxLength={160}
                  showCount
                  value={formValues.description}
                  placeholder="Enter description (optional)"
                  onChange={(event) => setField('description', event.target.value)}
                />

                <div className="inline-row">
                  <div>
                    <label>Sort Order</label>
                    <InputNumber
                      min={0}
                      value={formValues.sortOrder}
                      onChange={(value) => setField('sortOrder', value || 0)}
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div>
                    <label>Status</label>
                    <Select
                      value={formValues.status}
                      onChange={(value) => setField('status', value)}
                      options={[
                        { value: 'Active', label: 'Active' },
                        { value: 'Inactive', label: 'Inactive' }
                      ]}
                    />
                  </div>
                </div>

                <div className="drawer-actions">
                  <Button onClick={onClose}>Cancel</Button>
                  <Button type="primary" onClick={handleSave}>Save Category</Button>
                </div>
      </div>
    </Drawer>
  )
}

export default AddCategoryDrawer
